import React, { useState } from "react";
import { LoaderFunctionArgs, useParams } from "react-router-dom";

import type { MeetingModelJson } from "@performyard/service-contracts";
import { ADMIN_ROLES } from "@performyard/utils-isomorphic";

import PageTitle from "../../../components/PageTitle";
import { EditMeetingModal } from "../MeetingsTable/components/modals";
import AgendaItemsSection from "./components/AgendaItemsSection";
import MeetingDetailsActionItems from "./components/MeetingDetailsActionItems";
import MeetingDetailsBreadcrumbs from "./components/MeetingDetailsBreadcrumbs";
import MeetingDetailsHeader from "./components/MeetingDetailsHeader";
import MeetingDetailsNotes from "./components/MeetingDetailsNotes";
import MeetingGoalsAccordion from "./components/MeetingGoalsAccordion";
import MeetingTranscriptSection from "./components/MeetingTranscriptSection";
import PastMeetingNotesAccordion from "./components/PastMeetingNotesAccordion";

import type { UserAssignmentContext } from "./types";

import { useCurrentUser, useMeetingDetails } from "../../../api/hooks";
import { getFeatureFlagQuery, useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
import { useManagerDirectReportMeeting, useMeetingEvents } from "./hooks";

import { currentUserQuery, meetingDetailsQuery } from "../../../api/queries";
import { SimplifiedQuery, dataLoader } from "../../../api/queries/dataLoader";

import ErrorPage from "../../Error";
import { config } from "./config";
import { MeetingProvider } from "./context";
import { UserAssignmentModal } from "./modals";

/**
 * Data loader for the meeting details route.
 * Prefetches meeting data and feature flags.
 */
export const meetingDetailsLoader = ({ params }: LoaderFunctionArgs): ReturnType<typeof dataLoader> => {
	const meetingId = params.meetingId;

	const queries: (() => SimplifiedQuery)[] = [];

	if (meetingId) {
		queries.push(
			() => meetingDetailsQuery(meetingId),
			() => getFeatureFlagQuery()
		);
	}

	return dataLoader([[currentUserQuery], queries]);
};

/**
 * Main component for displaying individual meeting details.
 * Handles access control and renders the meeting content.
 * Organized into left section (details, notes, actions) and right section (agenda, past meetings).
 */
const MeetingDetails = (): React.ReactElement | null => {
	const { meetingId } = useParams();

	const { data: currentUser } = useCurrentUser();
	const { data: meetingDetails, isLoading: isMeetingDetailsLoading } = useMeetingDetails(meetingId || "");
	const { value: ffMeetings, query: ffMeetingsQuery } = useFeatureFlagQuery("ff-is-meetings-enabled", false);

	const [editingMeeting, setEditingMeeting] = useState<MeetingModelJson | null>(null);
	const [userAssignment, setUserAssignment] = useState<UserAssignmentContext>(null);

	// Extract data for use in hooks and rendering
	const meeting = meetingDetails?.meeting;
	const workspace = meetingDetails?.workspace;
	const participantNames = meetingDetails?.participantNames;

	const userId = currentUser?.me?.authorization?.payload.user_id || "";
	const userRole = currentUser?.me?.authorization?.payload.user_primary_role || "";

	const isAdmin = ADMIN_ROLES.includes(userRole);
	const isParticipant = workspace?.participants?.some((participant) => participant.userId === userId) || false;

	const isManagerDirectReportMeeting = useManagerDirectReportMeeting({
		meeting,
		userId,
		isAdmin,
		isParticipant,
	});

	// Subscribe to real-time meeting events via SSE
	useMeetingEvents(meeting?._id || "", userId);

	// Early returns for loading and error states
	if (isMeetingDetailsLoading || ffMeetingsQuery.isLoading) {
		return null;
	}

	if (!ffMeetings) {
		return <ErrorPage code={403} />;
	}

	if (!meeting || !workspace) {
		return <ErrorPage code={404} />;
	}

	const hasAccess = isAdmin || isParticipant;

	if (!hasAccess) {
		return <ErrorPage code={403} />;
	}

	// Compute derived values for rendering
	const isAdminNonParticipant = isAdmin && !isParticipant;
	const meetingCreatorId = meeting.created?.userId;

	// For admin non-participant, "other participant" is the one who is NOT the meeting creator
	// (since admin view shows the meeting creator's notes as the primary option)
	// For regular participants, "other participant" is the one who is NOT the current user
	const otherParticipant = isAdminNonParticipant
		? workspace.participants?.find((p) => p.userId !== meetingCreatorId)
		: workspace.participants?.find((p) => p.userId !== userId);
	const participantName = otherParticipant?.name || "Participant";
	// Server computes isHistoricalMeeting using participation history intervals.
	const isHistoricalMeeting = meetingDetails?.isHistoricalMeeting ?? false;

	const isInteractionDisabled =
		isAdminNonParticipant || meeting.status === "archived" || meeting.status === "canceled" || isHistoricalMeeting;

	return (
		<>
			<PageTitle title={meeting.name} />
			<MeetingProvider
				value={{
					meeting,
					userId,
					setEditingMeeting,
					setUserAssignment,
					participantId: otherParticipant?.userId,
					participantName,
					workspace,
					isInteractionDisabled,
					isAdminView: isAdminNonParticipant,
					isAdmin,
					isManagerDirectReportMeeting,
					isHistoricalMeeting,
					participantNames,
				}}
			>
				<div className={config.layoutClasses.pageContainer}>
					<div className={config.layoutClasses.leftColumn}>
						<div className={config.layoutClasses.leftColumnInner}>
							<div className="flex flex-col gap-4">
								<MeetingDetailsBreadcrumbs
									workspaceName={workspace.name}
									workspaceId={workspace._id}
									meetingName={meeting.name}
									isAdmin={isAdminNonParticipant}
								/>
								<MeetingDetailsHeader />
							</div>
							<MeetingDetailsNotes />
							<div className="h-px w-full bg-neutral-2" />
							<MeetingDetailsActionItems />
						</div>
					</div>
					<div className={config.layoutClasses.rightColumn}>
						<div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
							<AgendaItemsSection />
							{!isAdminNonParticipant && (
								<>
									<PastMeetingNotesAccordion
										workspaceId={workspace._id}
										currentMeetingStartDate={meeting.startDateTime}
										currentMeetingId={meeting._id}
									/>
									<MeetingGoalsAccordion />
									<MeetingTranscriptSection />
								</>
							)}
						</div>
					</div>
				</div>
				<EditMeetingModal
					isOpen={Boolean(editingMeeting)}
					handleClose={() => setEditingMeeting(null)}
					meeting={editingMeeting}
				/>
				<UserAssignmentModal
					isOpen={Boolean(userAssignment)}
					onClose={() => setUserAssignment(null)}
					assignmentContext={userAssignment}
					assignerId={userId}
					workspace={workspace}
				/>
			</MeetingProvider>
		</>
	);
};

export default MeetingDetails;
